import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { EmptyState, Table, TableBody, TableCell, TableHeader, TableRow, cn } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { DocumentAnalysisSkillListItem, SystemSkillView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { CalendarIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { FilterBar } from '@/components/filter-bar';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';
import { SEARCH_PARAM, useListParams } from '@/lib/list-view/use-list-params';

// The list loader also fetches the pinned system fallback (IMM-469.2) so the
// page can render it above the per-type table. A small typed shape keeps the two
// resources distinct — the fallback is a single global entry, not a category row.
export interface DocumentAnalysisSkillsLoaderData {
  readonly fallback: SystemSkillView;
  readonly skills: ReadonlyArray<DocumentAnalysisSkillListItem>;
  readonly columnPreference: ColumnPreference;
}

export const SYSTEM_SKILL_FALLBACK_PATH = '/admin/skills/system/document-analysis-fallback';

export const documentAnalysisSkillsLoaderParams = [] as const;

export async function documentAnalysisSkillsLoader(): Promise<DocumentAnalysisSkillsLoaderData> {
  const [listResult, fallbackResult, columnPreference] = await Promise.all([
    apiClient.adminDocumentAnalysisSkills.list(),
    apiClient.adminSystemSkills.get(),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.documentAnalyses),
  ]);

  if (!listResult.ok) {
    throw new Response('Failed to load document-analysis skills', { status: listResult.status });
  }

  if (!fallbackResult.ok) {
    throw new Response('Failed to load the fallback skill', { status: fallbackResult.status });
  }

  return { fallback: fallbackResult.body, skills: listResult.body.data, columnPreference };
}

const STATUS_BADGE_CLASS: Record<DocumentAnalysisSkillListItem['status'], string> = {
  active: 'bg-success-50 text-success-700',
  inactive: 'bg-subtle text-text-tertiary',
  unconfigured: 'bg-subtle text-text-tertiary',
};

function useDocumentAnalysisColumns(): ReadonlyArray<OperatorColumn<DocumentAnalysisSkillListItem>> {
  const t = useTranslations('pages.admin.skills.document-analyses');
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        cell: (skill) => t(`category.${skill.category}`),
        sortValue: (skill) => t(`category.${skill.category}`),
      },
      {
        id: 'status',
        label: t('columns.status'),
        icon: TagIcon,
        headClassName: 'w-[150px]',
        cell: (skill) => <StatusBadge status={skill.status} label={t(`status.${skill.status}`)} />,
        sortValue: (skill) => t(`status.${skill.status}`),
      },
      {
        id: 'lastEdited',
        label: t('columns.last-edited'),
        icon: CalendarIcon,
        cellClassName: 'text-text-tertiary',
        cell: (skill) =>
          skill.lastEditedByName !== null && skill.lastEditedAt !== null
            ? t('last-edited-by', {
                name: skill.lastEditedByName,
                date: intl.formatDate(skill.lastEditedAt, { dateStyle: 'short' }),
              })
            : t('never-edited'),
        sortValue: (skill) =>
          skill.lastEditedByName !== null && skill.lastEditedAt !== null ? Date.parse(skill.lastEditedAt) : null,
      },
    ],
    [t, intl],
  );
}

export function DocumentAnalysisSkillsPage() {
  const t = useTranslations('pages.admin.skills.document-analyses');
  const tFallback = useTranslations('pages.admin.skills.document-analyses.fallback');
  const intl = useIntl();
  const { fallback, skills, columnPreference } = useLoaderData() as DocumentAnalysisSkillsLoaderData;
  const navigate = useNavigate();

  const { params, setParams } = useListParams();
  const query = params.get(SEARCH_PARAM) ?? '';

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    if (needle === '') return skills;

    return skills.filter((skill) => t(`category.${skill.category}`).toLowerCase().includes(needle));
  }, [skills, query, t]);
  const catalogue = useDocumentAnalysisColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.documentAnalyses,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  const fallbackAttribution =
    fallback.lastEditedByName !== null
      ? tFallback('last-edited-by', {
          name: fallback.lastEditedByName,
          date: intl.formatDate(fallback.lastEditedAt, { dateStyle: 'short' }),
        })
      : tFallback('system-attribution');

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={TagIcon} />} />
      {/* The pinned fallback entry sits above the filter bar and the per-type
          table: it is a single global entry, so it is never filtered by the
          search box and never falls into a taxonomy group. */}
      <div className="p-6 pb-0">
        <button
          type="button"
          onClick={() => navigate(SYSTEM_SKILL_FALLBACK_PATH)}
          className={cn(
            'flex w-full items-start justify-between gap-4 rounded-lg border border-border bg-subtle/40 px-4 py-3 text-left',
            'transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive',
          )}
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text-primary">{tFallback('title')}</span>
              <StatusBadge status="active" label={tFallback('status')} />
            </div>
            <p className="text-sm text-text-tertiary">{tFallback('subtitle')}</p>
          </div>
          <span className="shrink-0 text-xs text-text-tertiary">{fallbackAttribution}</span>
        </button>
      </div>
      <FilterBar
        searchValue={query}
        searchPlaceholder={t('search')}
        onSearchChange={(next, commit) => setParams({ [SEARCH_PARAM]: next }, { replace: commit.continuing })}
        trailing={<ColumnsTrigger {...table.columns.trigger} />}
      />
      {filtered.length === 0 ? (
        <div className="p-6">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow {...table.columns.headerRowProps}>
              {table.columns.visibleColumns.map((column) => (
                <OperatorColumnHead key={column.id} column={column} table={table} />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.sorted.map((skill, index) => {
              const isGroupStart =
                table.sort === null && (index === 0 || table.sorted[index - 1]!.group !== skill.group);

              return (
                <Fragment key={skill.category}>
                  {isGroupStart && (
                    <TableRow>
                      <TableCell
                        colSpan={table.columns.visibleColumns.length}
                        className="bg-subtle/40 text-xs font-semibold uppercase tracking-wide text-text-tertiary"
                      >
                        {t(`category-group.${skill.group}`)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow interactive onClick={() => navigate(`/admin/skills/document-analyses/${skill.category}`)}>
                    {table.columns.visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                        style={column.cellStyle}
                      >
                        {column.cell(skill)}
                      </TableCell>
                    ))}
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function StatusBadge({
  status,
  label,
}: {
  readonly status: DocumentAnalysisSkillListItem['status'];
  readonly label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        STATUS_BADGE_CLASS[status],
      )}
    >
      {label}
    </span>
  );
}
